import router from 'next/router';
import { ApolloClient, from } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import {
  GetDefaultAccountDocument,
  GetDefaultAccountQuery,
} from 'pages/api/getDefaultAccount.generated';
import { signOutAndClear } from 'src/lib/clearSessionData';
import { reportGraphQLError, reportNetworkError } from 'src/lib/dataDog';
import snackNotifications from '../../components/Snackbar/Snackbar';
import { dispatch } from '../analytics';
import {
  isAccountListNotFoundError,
  replaceUrlAccountList,
} from './accountListRedirect';
import { createCache } from './cache';
import { batchLink, makeAuthLink } from './link';
import {
  SuppressErrorsContext,
  isGraphQLErrorSuppressed,
} from './suppressErrors';

const cache = createCache();

const makeClient = (apiToken: string) => {
  const client = new ApolloClient({
    link: from([
      makeAuthLink(apiToken),
      onError(({ graphQLErrors, networkError, operation }) => {
        const suppressContext: SuppressErrorsContext = operation.getContext();

        // Apollo passes a failed response's `errors` through unchecked, so it
        // may not be an array. Throwing here would leave the query loading forever.
        const errors = Array.isArray(graphQLErrors) ? graphQLErrors : [];
        errors.forEach((graphQLError) => {
          if (graphQLError?.extensions?.code === 'AUTHENTICATION_ERROR') {
            signOutAndClear(client, {
              redirect: true,
              callbackUrl: 'signOut',
            });
          } else if (isAccountListNotFoundError(graphQLError)) {
            client
              .query<GetDefaultAccountQuery>({
                query: GetDefaultAccountDocument,
              })
              .then((response) => {
                // eslint-disable-next-line no-console
                console.log('Incorrect accountListId provided. Redirecting.');
                router.replace(
                  replaceUrlAccountList(
                    window.location.pathname,
                    response.data.user.defaultAccountList,
                  ),
                );
              });
          } else if (!isGraphQLErrorSuppressed(suppressContext, graphQLError)) {
            snackNotifications.error(graphQLError.message);
            reportGraphQLError(graphQLError, operation);
          }
        });

        if (networkError && networkError?.name !== 'AbortError') {
          dispatch('mpdx-api-error');
          snackNotifications.error(networkError.message);
          reportNetworkError(networkError, operation);
        }
      }),
      batchLink,
    ]),
    cache,
    assumeImmutableResults: true,
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
      },
    },
  });
  return client;
};

export default makeClient;
